import { Link, useSearchParams } from "react-router-dom";
import { specials } from "@/mocks/specials";
import { articles } from "@/mocks/articles";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import type { Article } from "@/types/article";

export default function Specials() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get("id") || specials[0].id;
  const current = specials.find((s) => s.id === activeId) || specials[0];

  useDocumentMeta({
    title: `${current.title} - 黑丝时尚专题`,
    description: current.intro,
    keywords: "黑丝专题,潮流资讯,搭配指南,丝尚趋势",
  });

  const pick = (ids: string[]): Article[] =>
    ids.map((id) => articles.find((a) => a.id === id)).filter((a): a is Article => Boolean(a));

  const otherSpecials = specials.filter((s) => s.id !== current.id);

  return (
    <div className="pb-16">
      <section className="relative h-[420px] w-full overflow-hidden md:h-[520px]">
        <img
          src={current.cover}
          alt={`${current.title} 专题主图`}
          title={`${current.title} - 黑丝时尚专题`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/25" />
        <div className="absolute inset-0">
          <div className="mx-auto flex h-full max-w-[1200px] flex-col justify-end px-4 pb-12 md:px-8 md:pb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-accent-400">{current.subtitle}</p>
            <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {current.title}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
              {current.intro}
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1200px] px-4 md:px-8">
        <div className="mt-6 flex flex-wrap gap-2">
          {specials.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSearchParams({ id: s.id })}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                s.id === current.id
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <Breadcrumb items={[{ label: "潮流专题", to: "/specials" }, { label: current.title }]} />
        </div>

        <div className="mt-10 flex flex-col gap-14">
          {current.groups.map((group, gi) => {
            const groupArticles = pick(group.articleIds);
            return (
              <section key={group.title}>
                <div className="flex items-start gap-4 border-b border-foreground-200/70 pb-5">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-heading text-lg font-bold ${
                      gi % 2 === 0 ? "bg-primary-500 text-background-50" : "bg-accent-500 text-foreground-950"
                    }`}
                  >
                    {gi + 1}
                  </span>
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-foreground-950">{group.title}</h2>
                    <p className="mt-1 text-sm text-foreground-600">
                      <i className="ri-lightbulb-line mr-1 text-accent-600" />
                      {group.reason}
                    </p>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {groupArticles.map((item) => (
                    <ArticleCard key={item.id} article={item} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <section className="mt-16 border-t border-foreground-200/70 pt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">相关专题</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {otherSpecials.map((s) => (
              <Link
                key={s.id}
                to={`/specials?id=${s.id}`}
                className="group relative h-56 overflow-hidden rounded-lg"
              >
                <img
                  src={s.cover}
                  alt={`${s.title} 专题`}
                  title={`${s.title} - 黑丝时尚专题`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-400">{s.subtitle}</span>
                  <h3 className="mt-2 font-heading text-xl font-bold text-background-50">{s.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}